import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { MessageCircle, BookOpen } from "lucide-react";
import { IMAGES, CONTACT, MENU_LINK } from "../../lib/constants";

const PARTICLE_COUNT = 28;

export const Hero = () => {
  // Stable random particles
  const particles = useMemo(
    () =>
      Array.from({ length: PARTICLE_COUNT }, (_, i) => {
        const seed = i * 7919;
        const rnd = (n) => ((seed * (n + 1)) % 1000) / 1000;
        return {
          id: i,
          left: rnd(1) * 100,
          bottom: -10 - rnd(2) * 20,
          size: 1.5 + rnd(3) * 2.5,
          duration: 14 + rnd(4) * 18,
          delay: -rnd(5) * 20,
          dx: (rnd(6) - 0.5) * 80,
          opacity: 0.35 + rnd(7) * 0.45,
        };
      }),
    []
  );

  return (
    <section
      id="inicio"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #000000 0%, #0a0000 35%, #1a0000 65%, #240505 100%)",
      }}
      data-testid="hero-section"
    >
      {/* Soft gold corner glows */}
      <div className="absolute -top-40 -left-40 w-[640px] h-[640px] bg-[#C9A84C] opacity-[0.14] blur-[160px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[640px] h-[640px] bg-[#C9A84C] opacity-[0.10] blur-[160px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-[#8B0000] opacity-[0.20] blur-[180px] pointer-events-none" />

      {/* Inner gold vignette ring */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ boxShadow: "inset 0 0 220px rgba(201, 168, 76, 0.18)" }}
      />

      {/* Floating gold particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {particles.map((p) => (
          <span
            key={p.id}
            className="absolute rounded-full bg-[#C9A84C] animate-particle-rise"
            style={{
              left: `${p.left}%`,
              bottom: `${p.bottom}px`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              opacity: p.opacity,
              animationDuration: `${p.duration}s`,
              animationDelay: `${p.delay}s`,
              boxShadow: "0 0 8px rgba(201, 168, 76, 0.9)",
              "--dx": `${p.dx}px`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-28 pb-20">
        {/* Logo with float + glow */}
        <div className="relative inline-block mb-2" data-testid="hero-logo-wrapper">
          {/* Pulsing gold halo */}
          <div className="absolute inset-0 m-auto w-[80%] h-[80%] bg-gradient-radial from-[#C9A84C] via-[#C9A84C]/40 to-transparent rounded-full blur-3xl animate-glow-pulse-gold"
               style={{ background: "radial-gradient(circle, rgba(201,168,76,0.55) 0%, rgba(201,168,76,0.18) 40%, transparent 70%)" }}
          />
          <motion.img
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            src={IMAGES.logo}
            alt="O Padrinho — Restaurante e Lanchonete"
            className="relative h-[200px] sm:h-[240px] md:h-[280px] w-auto mx-auto"
            style={{
              filter:
                "drop-shadow(0 0 18px rgba(201,168,76,0.55)) drop-shadow(0 12px 28px rgba(0,0,0,0.7))",
            }}
            data-testid="hero-logo"
          />
          {/* Pulsing gold floor shadow */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-4 w-[70%] h-6 rounded-full bg-[#C9A84C] opacity-50 blur-2xl animate-glow-pulse-gold" />
        </div>

        {/* Sub-title gold serif */}
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="font-serif-display italic text-2xl sm:text-3xl md:text-4xl font-medium tracking-wide mt-8"
          style={{
            color: "#C9A84C",
            textShadow: "0 0 18px rgba(201,168,76,0.45)",
          }}
          data-testid="hero-title"
        >
          Restaurante e Lanchonete
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="gold-ornament mt-5 mb-6"
        />

        {/* Slogan */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="text-base md:text-xl text-white max-w-2xl mx-auto leading-relaxed"
          style={{ textShadow: "0 0 24px rgba(255, 240, 220, 0.35), 0 2px 8px rgba(0,0,0,0.7)" }}
          data-testid="hero-subtitle"
        >
          28 anos alimentando Sorriso com{" "}
          <span className="text-gold italic" style={{ textShadow: "0 0 22px rgba(201,168,76,0.6)" }}>
            sabor e tradição
          </span>
          .
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <a
            href={CONTACT.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-semibold text-base shadow-[0_8px_24px_rgba(37,211,102,0.4)]"
            data-testid="hero-whatsapp-cta"
          >
            <MessageCircle size={18} fill="currentColor" strokeWidth={0} />
            Fale pelo WhatsApp
          </a>
          <a
            href={MENU_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="relative inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-semibold text-base text-[#2a1810] animate-border-shine"
            style={{
              background:
                "linear-gradient(180deg, #f3d271 0%, #C9A84C 50%, #a07b22 100%)",
              border: "1px solid #fff2c2",
            }}
            data-testid="hero-menu-cta"
          >
            <BookOpen size={18} />
            Ver Cardápio
          </a>
        </motion.div>
      </div>

      {/* Bottom fade into bege section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#F5F0E8] pointer-events-none" />
    </section>
  );
};

export default Hero;
